import { type FC } from "react";
import { Modal, Spin } from "antd";
import {
  CloseOutlined,
  LoadingOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import AgOperationFailed from "../../../assets/images/AgOperationError.svg";
import "./AgOperationDataLoader.less";

export interface AgOperationDataLoaderProps {
  /** Controls modal visibility. */
  open: boolean;
  /** Invoked when the user closes the modal. */
  onClose: () => void;
  errorMessage: string | undefined;
}

/**
 * Loading state modal shown while an ag-operation data export is being
 * prepared. Renders the "Export" header and a centered spinner over dashed
 * crosshair guides.
 */
const AgOperationDataLoader: FC<AgOperationDataLoaderProps> = ({
  open,
  onClose,
  errorMessage,
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      className="ag-operation-data-loader"
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={640}
      maskClosable={false}
      data-testid="ag-operation-data-loader"
    >
      <div className="ag-operation-data-loader__header">
        <div className="ag-operation-data-loader__title">
          <span className="ag-operation-data-loader__icon">
            <UploadOutlined />
          </span>
          <span className="ag-operation-data-loader__title-text">
            {formatTranslation(t("trials.export"))}
          </span>
        </div>
        <button
          type="button"
          className="ag-operation-data-loader__close"
          onClick={onClose}
          aria-label={formatTranslation(t("trials.close"))}
          data-testid="ag-operation-data-loader-close"
        >
          <CloseOutlined />
        </button>
      </div>

      <div className="ag-operation-data-loader__body">
        <div className="ag-operation-data-loader__spinner">
          {errorMessage ? (
            <div className="ag-operation-data-loader__error-container">
              <img src={AgOperationFailed} alt="AgOperation Failed" />
              <span className="ag-operation-data-loader__error-container_title">
                {formatTranslation(t("common.error.console.agOperation"))}.
              </span>
              <span className="ag-operation-data-loader__error-container_subTitle">
                {formatTranslation(
                  t("common.error.console.agOperation_subtitle")
                )}
                .
              </span>
            </div>
          ) : (
            <Spin
              indicator={<LoadingOutlined spin style={{ fontSize: 28 }} />}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AgOperationDataLoader;
